import { Modal, Button, Form, InputNumber, Radio, Typography } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import { useMainContext } from '../../hooks/useMainContext';
import { ObstacleType } from '../../config';
import './ObstacleForm.css';
import Draggable from 'react-draggable';
import { TbX, TbArrowsMove } from 'react-icons/tb';
import { SCALEFACTOR_FOR_CONVERSION } from '../../constant';

export const ObstacleForm = observer(() => {
    const { closetManager } = useMainContext();
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (closetManager.shouldObstacleFormOpen()) {
            setModalPosition({ x: 0, y: 0 });
        }
    }, [closetManager.shouldObstacleFormOpen()]);

    const { control, handleSubmit, reset, setValue, watch } = useForm({
        defaultValues: {
            obstacleType: ObstacleType.RECTANGLE,
            height: 12, // Default to 12 inches
            width: 12,
            radius: 0,
            rotation: 0,
        },
    });

    const obstacleType = watch('obstacleType');

    // Handle selectedObstacle changes (initial load or edit mode)
    useEffect(() => {
        if (closetManager.selectedObstacle) {
            const scaleFactor =
                closetManager.getScaleFactorForAnnotationDisplayByClosetId(
                    closetManager.selectedPageNo,
                    closetManager.selectedObstacle.closetId,
                    true,
                );

            const height = parseFloat(
                closetManager.selectedObstacle.data.height /
                    (SCALEFACTOR_FOR_CONVERSION * scaleFactor),
            ).toFixed(2);
            const width = parseFloat(
                closetManager.selectedObstacle.data.width /
                    (SCALEFACTOR_FOR_CONVERSION * scaleFactor),
            ).toFixed(2);
            const radius = parseFloat(
                closetManager.selectedObstacle.data.radius /
                    (SCALEFACTOR_FOR_CONVERSION * scaleFactor),
            ).toFixed(2);
            const rotation = parseFloat(
                closetManager.selectedObstacle.data.rotation % 360 || 0,
            ).toFixed(2);

            const selectedType = closetManager.selectedObstacle.obstacleType;
            setValue('obstacleType', selectedType);
            setValue(
                'height',
                selectedType === ObstacleType.RECTANGLE
                    ? parseFloat(height)
                    : 0,
            );
            setValue(
                'width',
                selectedType === ObstacleType.RECTANGLE ? parseFloat(width) : 0,
            );
            setValue(
                'radius',
                selectedType === ObstacleType.CIRCLE ? parseFloat(radius) : 0,
            );
            setValue('rotation', rotation);
        } else {
            reset();
        }
    }, [closetManager.selectedObstacle, closetManager.obstacleForm]);

    // Handle obstacleType switch
    useEffect(() => {
        if (obstacleType === ObstacleType.RECTANGLE) {
            setValue('height', 12);
            setValue('width', 12);
            setValue('radius', 0);
        } else if (obstacleType === ObstacleType.CIRCLE) {
            setValue('height', 0);
            setValue('width', 0);
            setValue('radius', 12);
        }
    }, [obstacleType, setValue]);

    // Handle inch input change to ensure 2 decimal places
    const handleInchChange = (field, value) => {
        const parsedValue = parseFloat(value) || 0;
        setValue(field, parseFloat(parsedValue.toFixed(2)));
    };

    const onSubmit = (data) => {
        if (
            (data.obstacleType === ObstacleType.RECTANGLE &&
                (data.height <= 0 || data.width <= 0)) ||
            (data.obstacleType === ObstacleType.CIRCLE && data.radius <= 0)
        ) {
            return;
        }
        const obstacleData = {
            obstacleType: data.obstacleType,
            height: `0' ${parseFloat(data.height).toFixed(2)}"`,
            width: `0' ${parseFloat(data.width).toFixed(2)}"`,
            radius: `0' ${parseFloat(data.radius).toFixed(2)}"`,
            rotation: parseFloat(data.rotation).toFixed(2),
        };

        if (!closetManager.obstacleEditMode) {
            closetManager.addObstacle(obstacleData);
        } else {
            closetManager.updateSelectedObstacle(obstacleData);
        }
        closetManager.setObstacleForm(false);
        closetManager.setObstacleMode(false);
        closetManager.setObstacleEditMode(false);
        closetManager.drawNewClosets();
        reset();
    };

    const closeModal = () => {
        closetManager.setObstacleForm(false);
        closetManager.setObstacleMode(false);
        closetManager.setObstacleEditMode(false);
        reset();
    };

    return (
        <Modal
            className="obstacle-form-modal"
            width={500}
            centered
            styles={{
                content: {
                    padding: '32px',
                    borderRadius: '32px',
                },
            }}
            modalRender={(modal) => (
                <Draggable
                    handle=".drag-button"
                    cancel=".ant-input-number, .ant-radio-group, .ant-btn:not(.drag-button)"
                    position={modalPosition}
                    onStop={(_, data) => {
                        setModalPosition({ x: data.x, y: data.y });
                    }}>
                    <div>{modal}</div>
                </Draggable>
            )}
            onCancel={closeModal}
            footer={null}
            closable={false}
            open={closetManager.shouldObstacleFormOpen()}>
            <div className="obstacle-form-wrapper">
                <div
                    className="border-b-[1px] border-theme-border pb-2"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 10,
                    }}>
                    <Typography.Title
                        level={4}
                        style={{
                            margin: 0,
                            fontSize: '32px',
                            lineHeight: '40px',
                            color: '#000000',
                        }}>
                        Obstacle Form
                    </Typography.Title>
                    <div style={{ display: 'flex' }}>
                        <Button
                            className="drag-button flex items-center justify-center p-0 w-[32px] h-[32px]"
                            style={{ cursor: 'move', border: 'none' }}>
                            <TbArrowsMove size={20} />{' '}
                        </Button>
                        <Button
                            type="text"
                            className="flex items-center justify-center p-0 w-[32px] h-[32px]"
                            onClick={closeModal}>
                            <TbX size={20} />
                        </Button>
                    </div>
                </div>
                <Form
                    layout="vertical"
                    className="door-form !font-favorit obstacle-form"
                    onFinish={handleSubmit(onSubmit)}>
                    <Form.Item label="Obstacle Type">
                        <Controller
                            name="obstacleType"
                            control={control}
                            render={({ field }) => (
                                <Radio.Group {...field}>
                                    {Object.entries(ObstacleType).map(
                                        ([key, value]) => (
                                            <Radio key={value} value={value}>
                                                {key}
                                            </Radio>
                                        ),
                                    )}
                                </Radio.Group>
                            )}
                        />
                    </Form.Item>

                    {obstacleType === ObstacleType.RECTANGLE && (
                        <>
                            <Form.Item label="Height">
                                <Controller
                                    name="height"
                                    control={control}
                                    rules={{
                                        required: 'Required',
                                        validate: (value) =>
                                            value > 0 ||
                                            'Height must be greater than zero',
                                    }}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <InputNumber
                                                {...field}
                                                addonAfter={'"'}
                                                className="number-input"
                                                placeholder="inches"
                                                min={0}
                                                step={0.01}
                                                precision={2}
                                                onChange={(value) =>
                                                    handleInchChange(
                                                        'height',
                                                        value,
                                                    )
                                                }
                                                status={
                                                    fieldState.invalid
                                                        ? 'error'
                                                        : ''
                                                }
                                            />
                                            {fieldState.error && (
                                                <p className="error-message">
                                                    {fieldState.error.message}
                                                </p>
                                            )}
                                        </>
                                    )}
                                />
                            </Form.Item>

                            <Form.Item label="Width">
                                <Controller
                                    name="width"
                                    control={control}
                                    rules={{
                                        required: 'Required',
                                        validate: (value) =>
                                            value > 0 ||
                                            'Width must be greater than zero',
                                    }}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <InputNumber
                                                {...field}
                                                placeholder="inches"
                                                addonAfter={'"'}
                                                className="number-input"
                                                min={0}
                                                step={0.01}
                                                precision={2}
                                                onChange={(value) =>
                                                    handleInchChange(
                                                        'width',
                                                        value,
                                                    )
                                                }
                                                status={
                                                    fieldState.invalid
                                                        ? 'error'
                                                        : ''
                                                }
                                            />
                                            {fieldState.error && (
                                                <p className="error-message">
                                                    {fieldState.error.message}
                                                </p>
                                            )}
                                        </>
                                    )}
                                />
                            </Form.Item>
                        </>
                    )}

                    {obstacleType === ObstacleType.CIRCLE && (
                        <Form.Item label="Radius">
                            <Controller
                                name="radius"
                                control={control}
                                rules={{
                                    required: 'Required',
                                    validate: (value) =>
                                        value > 0 ||
                                        'Radius must be greater than zero',
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <InputNumber
                                            {...field}
                                            placeholder="inches"
                                            addonAfter={'"'}
                                            className="number-input"
                                            min={0}
                                            step={0.01}
                                            precision={2}
                                            onChange={(value) =>
                                                handleInchChange(
                                                    'radius',
                                                    value,
                                                )
                                            }
                                            status={
                                                fieldState.invalid
                                                    ? 'error'
                                                    : ''
                                            }
                                        />
                                        {fieldState.error && (
                                            <p className="error-message">
                                                {fieldState.error.message}
                                            </p>
                                        )}
                                    </>
                                )}
                            />
                        </Form.Item>
                    )}

                    {obstacleType === ObstacleType.RECTANGLE && (
                        <Form.Item label="Rotation">
                            <Controller
                                name="rotation"
                                control={control}
                                rules={{
                                    validate: (value) =>
                                        (value >= 0 && value <= 360) ||
                                        'Rotation must be between 0 and 360 degrees',
                                }}
                                render={({ field, fieldState }) => (
                                    <InputNumber
                                        {...field}
                                        addonAfter="°"
                                        className="number-input"
                                        placeholder="degrees"
                                        step={1}
                                        precision={0}
                                        status={
                                            fieldState.invalid ? 'error' : ''
                                        }
                                    />
                                )}
                            />
                        </Form.Item>
                    )}

                    <div className="form-actions">
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="bg-theme-orange text-theme-white !rounded-[36px] text-[16px] w-[140px] h-[40px] !!hover:bg-theme-orange-hover !shadow-none save-changes-button">
                            Confirm
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
});
